import { useMemo, useState } from 'react';
import type { Inputs, PostFormat } from '@/lib/likesBenchmark';
import { FORMATS, evaluate, reportText } from '@/lib/likesBenchmark';

const EMPTY: Inputs = {
  format: null,
  followers: '',
  impressions: '',
  reactions: '',
  comments: '',
  reposts: '',
  clicks: '',
};

// L'exemple chiffré du guide « taux d'engagement LinkedIn » : 73 ÷ 1 800 = 4,06 %.
const SAMPLE: Inputs = {
  format: 'image',
  followers: '3000',
  impressions: '1800',
  reactions: '40',
  comments: '6',
  reposts: '2',
  clicks: '25',
};

type NumberField = Exclude<keyof Inputs, 'format'>;

const FIELDS: { key: NumberField; label: string; hint: string }[] = [
  { key: 'followers', label: 'Abonnés', hint: 'Votre nombre d’abonnés' },
  { key: 'impressions', label: 'Impressions', hint: 'Sous le post : « Voir les statistiques »' },
  { key: 'reactions', label: 'Réactions', hint: 'Les likes, bravo, instructif…' },
  { key: 'comments', label: 'Commentaires', hint: 'Sans compter vos réponses' },
  { key: 'reposts', label: 'Republications', hint: 'Facultatif' },
  { key: 'clicks', label: 'Clics', hint: 'Facultatif, mais ils comptent' },
];

export function LikesCalculator() {
  const [input, setInput] = useState<Inputs>(EMPTY);
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => evaluate(input), [input]);
  const touched = Object.values(input).some((v) => v !== null && v !== '');

  const update = (patch: Partial<Inputs>) => {
    setInput((prev) => ({ ...prev, ...patch }));
    setCopied(false);
  };

  const copyResult = () => {
    try {
      navigator.clipboard
        .writeText(reportText(result))
        .then(() => setCopied(true))
        .catch(() => {
          // Écriture refusée par le navigateur : le bouton reste silencieusement inerte.
        });
    } catch {
      // Pas de Clipboard API (navigateur ancien, contexte non sécurisé) : on n'interrompt rien.
    }
  };

  const showResult = result.level !== 'incomplete';
  const showCopy = showResult && result.level !== 'invalid';

  return (
    <div className="nz-calc">
      <div className="nz-calc__label" id="nz-calc-format">
        Format du post
      </div>
      <div className="nz-sim__tabs nz-calc__formats" role="group" aria-labelledby="nz-calc-format">
        {(Object.keys(FORMATS) as PostFormat[]).map((key) => (
          <button
            key={key}
            type="button"
            className={`nz-sim__tab${input.format === key ? ' nz-sim__tab--on' : ''}`}
            aria-pressed={input.format === key}
            onClick={() => update({ format: input.format === key ? null : key })}
          >
            {FORMATS[key].label}
          </button>
        ))}
      </div>

      <div className="nz-calc__grid">
        {FIELDS.map((field) => (
          <div key={field.key} className="nz-calc__field">
            <label className="nz-sim__label" htmlFor={`nz-calc-${field.key}`}>
              {field.label}
            </label>
            <input
              id={`nz-calc-${field.key}`}
              className="nz-sim__input"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="0"
              value={input[field.key]}
              onChange={(event) => update({ [field.key]: event.target.value } as Partial<Inputs>)}
            />
            <small className="nz-calc__hint">{field.hint}</small>
          </div>
        ))}
      </div>

      <div className="nz-sim__bar">
        <button type="button" className="nz-wizard__back nz-sim__sample" onClick={() => setInput(touched ? EMPTY : SAMPLE)}>
          {touched ? 'Tout effacer' : 'Essayer avec un exemple'}
        </button>
      </div>

      <div className="nz-calc__result" aria-live="polite">
        {showResult ? (
          <>
            <h2 className={`nz-calc__headline nz-calc__headline--${result.level}`}>{result.headline}</h2>
            {result.rate !== null && result.level !== 'invalid' ? (
              <p className="nz-calc__rate">
                <strong>{result.rate.toFixed(2).replace('.', ',')} %</strong>
                <span>taux d’engagement sur impressions</span>
              </p>
            ) : null}
            <p>{result.summary}</p>
            {result.nextStep ? <p className="nz-wizard__tip">{result.nextStep}</p> : null}
            {result.caveats.length > 0 ? (
              <ul className="nz-calc__caveats">
                {result.caveats.map((caveat) => (
                  <li key={caveat}>{caveat}</li>
                ))}
              </ul>
            ) : null}
            {showCopy ? (
              <div className="nz-wizard__actions">
                <button type="button" className="nz-btn-secondary" onClick={copyResult}>
                  {copied ? 'Copié' : 'Copier le résultat'}
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <p className="nz-calc__empty">
            Renseignez vos réactions : le résultat se calcule en direct. Plus vous ajoutez de chiffres, plus la
            comparaison est précise.
          </p>
        )}
      </div>
    </div>
  );
}
